import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Chip,
    Drawer,
    IconButton,
    Link,
    Paper,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ArrowBack, Close } from '@mui/icons-material';
import LabelLayerChip from '../coding/LabelLayerChip';
import { LAYER_STYLES, SPEAKER_LABELS } from '../coding/codingColors';
import { NotFound } from '../layout/SystemState';
import { getResearchLesson, getUtteranceHistory } from '../../services/researchService';

const AMBER = '#FFF6E0';
// Machine layers first, the final label last, as in the frame
const SOURCE_ORDER = ['model', 'llm', 'original', 'annotation', 'adjudication', 'final'];
const FLAG_STYLE = {
    control: { color: '#4B5565', bgcolor: '#EDF0F2' },
    default: { color: '#8A5A00', bgcolor: AMBER },
};
const SOURCE_NAMES = { original: 'Original', model: 'Model', llm: 'LLM', annotation: 'Annotation', adjudication: 'Adjudication', final: 'Final' };
const when = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }) : '');
const time = (iso) => (iso ? new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }) : '');
const code = (l) => (l ? [l.ut, l.rc].filter(Boolean).join(' · ') : '—');
const top = (probs) => Object.entries(probs || {}).sort((a, b) => b[1] - a[1]).map(([c, p]) => `${c} ${p.toFixed(2).replace(/^0/, '')}`).join(' · ');

// What a history entry says beside its code, from the contract's fields only (frame 6)
const detailsOf = (entry) => {
    const lines = [];
    if (entry.why === 'resolved' && entry.resolution) {
        const parts = Object.entries(entry.resolution).map(([d, r]) => `${d.toUpperCase()} ${r.replace(/_/g, '-')}`);
        lines.push(`resolved · ${parts.join(' · ')}`);
        lines.push(Object.values(entry.resolution).includes('tie_break')
            ? 'written by the system when the tie-break locked'
            : 'written by the system when both blind codes agreed');
    } else if (entry.source === 'adjudication') {
        const tieOnly = !entry.ut || !entry.rc;
        lines.push(tieOnly
            ? `coded · ${!entry.ut ? 'RC' : 'UT'} only (tie)`
            : `coded blind${entry.lockedAt ? ` · locked ${time(entry.lockedAt)}` : ''}${entry.revealedAt ? ` · revealed ${time(entry.revealedAt)}` : ''}`);
        const reasons = entry.reasonCodes && Object.entries(entry.reasonCodes).map(([d, r]) => `${d.toUpperCase()}: ${r.replace(/_/g, ' ')}`).join(' · ');
        const extra = [reasons && `reason ${reasons}`, entry.note && `note: “${entry.note}”`, entry.contextExpanded && 'expanded to lesson'].filter(Boolean).join(' · ');
        if (extra) lines.push(extra);
    } else if (entry.probabilities) {
        lines.push(`predicted · UT ${top(entry.probabilities.ut)} · RC ${top(entry.probabilities.rc)}`);
    } else if (entry.source === 'annotation') {
        lines.push('coded · whole lesson');
    } else {
        lines.push(entry.why);
    }
    if (entry.supersedes) lines.push(`corrects an earlier label`);
    return lines;
};

// One entry: layer and coder with the time, then the code beside what happened. The final label
// has a green rule; corrections are new entries, never edits.
const HistoryEntry = ({ entry }) => {
    const [first, ...rest] = detailsOf(entry);
    const isFinal = entry.source === 'final';
    return (
        <Box component="li" sx={{ listStyle: 'none', mt: 2, pl: 1.75, borderLeft: isFinal ? '3px solid' : '2px solid', borderColor: isFinal ? LAYER_STYLES.final.color : 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">{SOURCE_NAMES[entry.source]} · {entry.coder}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{when(entry.createdAt)}</Typography>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '84px 1fr', columnGap: 1.5, alignItems: 'baseline', mt: 0.25 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>{[entry.ut || '—', entry.rc || '—'].join(' · ')}</Typography>
                <Box>
                    <Typography variant="body2">{first}</Typography>
                    {rest.map((line) => <Typography key={line} variant="caption" component="p" color="text.secondary">{line}</Typography>)}
                </Box>
            </Box>
        </Box>
    );
};

const HistoryDrawer = ({ lessonId, utterance, finalLabel, onClose }) => {
    const [entries, setEntries] = useState(null);
    const [failed, setFailed] = useState(false);
    useEffect(() => {
        let alive = true;
        setEntries(null);
        setFailed(false);
        getUtteranceHistory(lessonId, utterance.seq).then((e) => alive && setEntries(e)).catch(() => alive && setFailed(true));
        return () => { alive = false; };
    }, [lessonId, utterance]);
    return (
        <Drawer anchor="right" open onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 460 }, p: 3 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Box>
                    <Typography variant="caption" color="text.secondary">Utterance {utterance.seq} · {SPEAKER_LABELS[utterance.speakerRole]}</Typography>
                    <Typography variant="h6" component="h2" sx={{ fontWeight: 700, lineHeight: 1.35, mt: 0.25 }}>“{utterance.text}”</Typography>
                </Box>
                <IconButton aria-label="Close" onClick={onClose} sx={{ mt: -0.5 }}><Close /></IconButton>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                {utterance.flag && <Chip size="small" label={`Item · ${utterance.flag}`} sx={{ borderRadius: 999, fontWeight: 600, ...(FLAG_STYLE[utterance.flag] || FLAG_STYLE.default) }} />}
                {finalLabel && <LabelLayerChip layer="final" code={code(finalLabel)} />}
            </Box>
            <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 1.5 }}>
                Newest first. Every label ever written for this utterance; none is changed or deleted.
            </Typography>
            {failed && <Alert severity="error" sx={{ mt: 2 }}>Couldn’t load the history.</Alert>}
            {!entries && !failed && [0, 1, 2].map((i) => <Skeleton key={i} height={72} />)}
            {entries && <Box component="ul" aria-label="Label history" sx={{ m: 0, p: 0 }}>{entries.map((e, i) => <HistoryEntry key={i} entry={e} />)}</Box>}
        </Drawer>
    );
};

// A pill that toggles: Show's filters, and the Layers groups in their layer's colours (frame 5)
const Toggle = ({ label, selected, onClick, layer }) => {
    const style = layer && LAYER_STYLES[layer];
    const on = style
        ? { bgcolor: style.background, color: style.color, border: `1px ${style.dashed ? 'dashed' : 'solid'} ${style.color}`, '&:hover': { bgcolor: style.background, filter: 'brightness(0.97)' } }
        : { bgcolor: (t) => alpha(t.palette.primary.main, 0.1), color: 'primary.main', border: '1px solid', borderColor: 'primary.main', '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.16) } };
    return (
        <Chip
            label={label}
            clickable
            onClick={onClick}
            aria-pressed={selected}
            variant={selected ? 'filled' : 'outlined'}
            sx={{
                height: 30,
                borderRadius: 999,
                fontWeight: 600,
                px: 0.5,
                transition: 'background-color 150ms, border-color 150ms, color 150ms',
                ...(selected ? on : { color: 'text.secondary', borderStyle: style?.dashed ? 'dashed' : 'solid' }),
            }}
        />
    );
};

// The heading of a layer's column: what it is, then who coded it
const subtitleOf = (layer) => (layer.source === 'final' ? (layer.runName ? `run ${layer.runName}` : '') : layer.coder);

// /research/lessons/:lessonId (wf-research-lessons 5–6, YES-272): one column per label layer, each the
// layer's current label. Researchers may see model and LLM output, probabilities and flags here;
// no annotation or adjudication endpoint serves this. Speaker role only, never source-row columns.
const LessonDetail = () => {
    const { lessonId } = useParams();
    const [data, setData] = useState(null);
    const [status, setStatus] = useState('loading');
    const [show, setShow] = useState('all');
    const [hidden, setHidden] = useState(new Set());
    const [open, setOpen] = useState(null);

    const load = useCallback(async () => {
        try {
            setData(await getResearchLesson(lessonId));
            setStatus('ready');
        } catch (err) {
            setStatus(err.response?.status === 404 ? 'notFound' : 'error');
        }
    }, [lessonId]);

    useEffect(() => {
        load();
    }, [load]);

    const allLayers = useMemo(
        () => [...(data?.layers || [])].sort((a, b) => SOURCE_ORDER.indexOf(a.source) - SOURCE_ORDER.indexOf(b.source)),
        [data]
    );
    const finalLayer = allLayers.find((l) => l.source === 'final');
    const differs = useCallback((u, layerId) => {
        const f = finalLayer && u.labels[finalLayer.layerId];
        const l = u.labels[layerId];
        return Boolean(f && l && layerId !== finalLayer.layerId && (f.ut !== l.ut || f.rc !== l.rc));
    }, [finalLayer]);
    const disagree = useCallback((u) => allLayers.some((layer) => differs(u, layer.layerId)), [allLayers, differs]);

    const rows = useMemo(() => {
        if (!data) return [];
        if (show === 'items') return data.utterances.filter((u) => u.flag);
        if (show === 'disagree') return data.utterances.filter(disagree);
        return data.utterances;
    }, [data, show, disagree]);

    if (status === 'notFound') return <NotFound />;
    if (status === 'error') {
        return (
            <Box sx={{ maxWidth: 600, mx: 'auto', mt: 6 }}>
                <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Try again</Button>}>Couldn’t load this lesson.</Alert>
            </Box>
        );
    }

    const lesson = data?.lesson;
    const layers = allLayers.filter((l) => !hidden.has(l.source));
    // Layers toggle a whole source at once: "LLM ×2" shows or hides both LLM columns
    const groups = SOURCE_ORDER.map((source) => ({ source, count: allLayers.filter((l) => l.source === source).length })).filter((g) => g.count);
    const toggleSource = (source) => setHidden((h) => { const n = new Set(h); if (n.has(source)) n.delete(source); else n.add(source); return n; });
    const total = data?.utterances.length ?? 0;
    const shows = [
        { key: 'all', label: `All ${total}` },
        { key: 'items', label: `Items ${data ? data.utterances.filter((u) => u.flag).length : ''}` },
        { key: 'disagree', label: `Disagree ${data ? data.utterances.filter(disagree).length : ''}` },
    ];

    return (
        <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
            <Link component={RouterLink} to="/lessons" underline="hover" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.8125rem', fontWeight: 600 }}>
                <ArrowBack sx={{ fontSize: 16 }} /> Lessons
            </Link>
            <Typography variant="h2" component="h1" sx={{ mt: 0.5 }}>
                {lesson ? [lesson.code, lesson.name].filter(Boolean).join(' · ') : <Skeleton width={320} />}
            </Typography>
            {lesson && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
                    {[
                        lesson.source === 'import' ? `Study 1 import · bundle ${lesson.bundle}` : `Upload · opted in ${lesson.researchUse.at ? new Date(lesson.researchUse.at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}`,
                        `${lesson.utterances.toLocaleString('en-US')} utterances`,
                        lesson.stageRun && `run ${lesson.stageRun.name}`,
                        lesson.labels.final === lesson.utterances ? `final labels for all ${lesson.utterances}` : `${lesson.labels.final} final labels`,
                    ].filter(Boolean).join(' · ')}
                </Typography>
            )}

            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, md: 4 }, flexWrap: 'wrap', mb: 2 }}>
                    <Box role="group" aria-label="Show" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Show</Typography>
                        {shows.map((s) => <Toggle key={s.key} label={s.label} selected={show === s.key} onClick={() => setShow(s.key)} />)}
                    </Box>
                    <Box role="group" aria-label="Layers" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Layers</Typography>
                        {groups.map((g) => (
                            <Toggle key={g.source} layer={g.source} label={`${SOURCE_NAMES[g.source]}${g.count > 1 ? ` ×${g.count}` : ''}`} selected={!hidden.has(g.source)} onClick={() => toggleSource(g.source)} />
                        ))}
                    </Box>
                </Box>

                <TableContainer sx={{ overflowX: 'auto', maxHeight: '70vh' }}>
                    <Table size="small" stickyHeader aria-label="Label layers" sx={{ minWidth: 760, '& th': { verticalAlign: 'top', bgcolor: 'background.default' }, '& td': { verticalAlign: 'top', py: 1.5 } }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>#</TableCell>
                                <TableCell>Speaker</TableCell>
                                <TableCell sx={{ minWidth: 240 }}>Utterance</TableCell>
                                <TableCell>Item</TableCell>
                                {layers.map((layer) => (
                                    <TableCell key={layer.layerId} sx={{ whiteSpace: 'nowrap' }}>
                                        {SOURCE_NAMES[layer.source]}
                                        {subtitleOf(layer) && <Typography variant="caption" component="span" display="block" color="text.secondary" sx={{ fontWeight: 400 }}>{subtitleOf(layer)}</Typography>}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {!data && [0, 1, 2, 3].map((i) => <TableRow key={i}><TableCell colSpan={6}><Skeleton /></TableCell></TableRow>)}
                            {rows.map((u) => (
                                <TableRow key={u.seq} hover onClick={() => setOpen(u)} sx={{ cursor: 'pointer' }} selected={open?.seq === u.seq}>
                                    <TableCell sx={{ color: 'text.secondary' }}>{u.seq}</TableCell>
                                    <TableCell>{SPEAKER_LABELS[u.speakerRole]}</TableCell>
                                    <TableCell>{u.text}</TableCell>
                                    <TableCell>
                                        {u.flag && <Chip size="small" label={u.flag} sx={{ borderRadius: 999, fontWeight: 600, ...(FLAG_STYLE[u.flag] || FLAG_STYLE.default) }} />}
                                    </TableCell>
                                    {layers.map((layer) => {
                                        const l = u.labels[layer.layerId];
                                        return (
                                            <TableCell key={layer.layerId} sx={{ whiteSpace: 'nowrap', px: 0.75 }}>
                                                <Box sx={{ px: 1, py: 0.5, borderRadius: 1, bgcolor: differs(u, layer.layerId) ? AMBER : 'transparent' }}>
                                                    <Typography variant="body2" sx={{ fontWeight: l ? 700 : 400, color: l ? 'text.primary' : 'text.secondary' }}>{code(l)}</Typography>
                                                    {typeof l?.p === 'number' && <Typography variant="caption" color="text.secondary">p {l.p.toFixed(2).replace(/^0/, '')}</Typography>}
                                                </Box>
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 1.5 }}>
                    Amber: differs from the final label. p: the model’s or LLM’s probability for its top RC code. {rows.length} of {total} rows; select one for its label history.
                </Typography>
            </Paper>

            {open && <HistoryDrawer lessonId={lessonId} utterance={open} finalLabel={finalLayer && open.labels[finalLayer.layerId]} onClose={() => setOpen(null)} />}
        </Box>
    );
};

export default LessonDetail;
